import { NEUTRAL, BLUE, GREEN, ORANGE, RED, YELLOW, AQUA, PURPLE } from './primitives'

// Semantic color tokens
export const paletteTokens = {
  primary: {
    default: BLUE[500],
    active: BLUE[700],
    emphasis: BLUE[800],
    inverted: BLUE[200],
  },
  secondary: {
    default: NEUTRAL[500],
    active: NEUTRAL[900],
    emphasis: NEUTRAL[900],
    inverted: NEUTRAL[400],
  },
  neutral: {
    default: NEUTRAL[200],
    active: NEUTRAL[500],
    emphasis: NEUTRAL[700],
    alternate: NEUTRAL[100],
    strong: NEUTRAL[900],
    muted: NEUTRAL[0],
  },
  accent: {
    blue: BLUE[500],
    green: GREEN[500],
    orange: ORANGE[500],
    red: RED[500],
    yellow: YELLOW[500],
    aqua: AQUA[500],
    purple: PURPLE[500],
  },
  status: {
    info: BLUE[600],
    critical: RED[600],
    success: GREEN[700],
    caution: YELLOW[700],
  },
  background: {
    default: NEUTRAL[0],
    alternate: NEUTRAL[100],
    emphasis: BLUE[100],
    critical: RED[100],
    success: GREEN[100],
    caution: ORANGE[100],
    inverted: AQUA[800],
    muted: NEUTRAL[50],
  },
  text: {
    default: NEUTRAL[900],
    alternate: NEUTRAL[0],
    support: NEUTRAL[700],
    brand: BLUE[700],
  },
  link: {
    default: AQUA[800],
    hover: BLUE[900],
    active: BLUE[900],
    visited: BLUE[700],
  },
  border: {
    default: NEUTRAL[200],
    emphasis: NEUTRAL[600],
    inverted: AQUA[800],
  },
  disabled: {
    default: NEUTRAL[200],
    emphasis: NEUTRAL[300],
    muted: NEUTRAL[50],
  },
  focus: {
    default: YELLOW[500],
  },
  surface: {
    default: NEUTRAL[0],
    subtle: NEUTRAL[100],
    subdued: NEUTRAL[300],
    emphasis: NEUTRAL[900],
  },
  danger: {
    default: RED[500],
    active: RED[700],
    emphasis: RED[800],
  },
  success: {
    default: GREEN[500],
    active: GREEN[700],
    emphasis: GREEN[800],
  },
} as const

// Typography tokens
export const typographyTokens = {
  base: {
    family: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: '16px',
    lineHeight: 1.5,
    kerning: 'normal',
  },
  weight: {
    thin: 100,
    light: 300,
    regular: 400,
    book: 450,
    medium: 500,
    semibold: 600,
    bold: 700,
    black: 900,
  },
  body: {
    family: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    weight: 400,
    size: {
      xs: '0.75rem',   // 12px
      sm: '0.875rem',  // 14px
      md: '1rem',      // 16px
      lg: '1.125rem',  // 18px
      xl: '1.25rem',   // 20px
    },
    lineHeight: {
      shortest: 1.2,
      short: 1.4,
      base: 1.5,
      tall: 1.6,
      tallest: 1.8,
    },
  },
  heading: {
    family: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    size: {
      xs: '0.875rem',  // 14px
      sm: '1rem',      // 16px
      md: '1.25rem',   // 20px
      lg: '1.5rem',    // 24px
      xl: '2rem',      // 32px
      '2xl': '2.5rem', // 40px
    },
    lineHeight: {
      shortest: 1.1,
      short: 1.2,
      base: 1.3,
      tall: 1.4,
      tallest: 1.5,
    },
  },
} as const

// Spacing tokens
export const spacingTokens = {
  10: '0.25rem',  // 4px
  20: '0.5rem',   // 8px
  30: '0.75rem',  // 12px
  40: '1rem',     // 16px
  50: '1.25rem',  // 20px
  60: '1.5rem',   // 24px
  70: '1.75rem',  // 28px
  80: '2rem',     // 32px
  90: '2.25rem',  // 36px
  100: '2.5rem',  // 40px
  110: '2.75rem', // 44px
  120: '3rem',    // 48px
  130: '3.25rem', // 52px
  140: '3.5rem',  // 56px
  150: '3.75rem', // 60px
  160: '4rem',    // 64px
} as const

// Scale tokens (for sizing)
export const scaleTokens = {
  30: '0.75rem',   // 12px
  40: '1rem',      // 16px
  50: '1.25rem',   // 20px
  60: '1.5rem',    // 24px
  70: '1.75rem',   // 28px
  80: '2rem',      // 32px
  90: '2.25rem',   // 36px
  100: '2.5rem',   // 40px
  110: '2.75rem',  // 44px
  120: '3rem',     // 48px
  130: '3.25rem',  // 52px
  140: '3.5rem',   // 56px
  150: '3.75rem',  // 60px
  160: '4rem',     // 64px
} as const

// Border tokens
export const borderTokens = {
  width: {
    sm: '1px',
    md: '2px',
    lg: '3px',
    xl: '4px',
  },
  radius: {
    sm: '0.25rem',   // 4px
    md: '0.5rem',    // 8px
    lg: '0.75rem',   // 12px
    xl: '1rem',      // 16px
    '2xl': '1.5rem', // 24px
    round: '50%',
  },
} as const

// Elevation tokens
export const elevationTokens = {
  bottom: {
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
} as const

// Icon tokens
export const iconTokens = {
  size: {
    xs: '0.75rem',   // 12px
    sm: '1rem',      // 16px
    md: '1.25rem',   // 20px
    lg: '1.5rem',    // 24px
  },
} as const

// Breakpoint tokens
export const breakpointTokens = {
  xs: { min: '0px', max: '639px' },
  sm: { min: '640px', max: '767px' },
  md: { min: '768px', max: '1023px' },
  lg: { min: '1024px', max: '1279px' },
  xl: { min: '1280px', max: '1535px' },
  '2xl': { min: '1536px', max: '9999px' },
} as const